// "Seen on Instagram" homepage video strip.
//
// IG WebView (≈62% of traffic) runs WKWebView, which OOM-kills the tab when too
// many videos decode at once, and blocks declarative <video autoplay>. So this
// component:
//   - never sets src up front (markup ships data-src, preload="none");
//   - loads + plays a video only when it scrolls into view, capped at
//     data-max-concurrent simultaneously (the most-visible win the slots);
//   - pauses AND unloads (removeAttribute src + load()) offscreen to release the
//     decode buffer;
//   - drives playback from JS (.play() on canplay, with .catch) instead of the
//     autoplay attribute, so a blocked promise degrades to the poster.
// If IntersectionObserver is missing, it degrades to loading everything; if
// there are no videos (posters only, before upload), it bails and leaves the
// posters in place. Nothing here throws into the page.

if (!(window.customElements && customElements.get('lm-seen-on-ig'))) {
  class LmSeenOnIg extends HTMLElement {
    connectedCallback() {
      this.videos = Array.from(this.querySelectorAll('.lm-ig__video'));
      // Poster-only (videos not uploaded yet) — nothing to enhance.
      if (!this.videos.length) return;

      this.maxConcurrent = parseInt(this.dataset.maxConcurrent, 10) || 2;
      if (this.maxConcurrent < 1) this.maxConcurrent = 1;

      this.active = new Set();
      this.visible = new Map(); // video -> intersectionRatio
      this.reduceMotion =
        window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this._onCanPlay = this.onCanPlay.bind(this);

      // No IntersectionObserver: degrade, don't break. Load and try to play
      // everything; posters cover anything the browser refuses to autoplay.
      if (!('IntersectionObserver' in window)) {
        this.videos.forEach((v) => {
          this.load(v);
          this.tryPlay(v);
        });
        return;
      }

      this.io = new IntersectionObserver(this.onIntersect.bind(this), {
        rootMargin: '200px 0px', // prime just before entry
        threshold: [0, 0.25, 0.5, 0.75, 1],
      });
      this.videos.forEach((v) => this.io.observe(v));
    }

    disconnectedCallback() {
      if (this.io) this.io.disconnect();
      if (this.videos) this.videos.forEach((v) => this.unload(v));
    }

    onIntersect(entries) {
      entries.forEach((entry) => {
        if (entry.isIntersecting && entry.intersectionRatio > 0) {
          this.visible.set(entry.target, entry.intersectionRatio);
        } else {
          this.visible.delete(entry.target);
        }
      });
      this.reconcile();
    }

    // Keep the maxConcurrent most-visible videos playing; unload the rest.
    // Runs on every intersection change so a video leaving view frees its slot
    // for another that's already on screen.
    reconcile() {
      const keep = new Set(
        Array.from(this.visible.entries())
          .sort((a, b) => b[1] - a[1])
          .slice(0, this.maxConcurrent)
          .map((pair) => pair[0])
      );

      this.active.forEach((v) => {
        if (!keep.has(v)) this.deactivate(v);
      });
      keep.forEach((v) => this.activate(v));
    }

    activate(v) {
      if (this.active.has(v)) return;
      this.active.add(v);
      this.load(v);
      if (this.reduceMotion) return;
      // canplay covers the case where src was just set and isn't buffered yet;
      // the immediate tryPlay covers an already-buffered (re-entering) video.
      v.addEventListener('canplay', this._onCanPlay);
      this.tryPlay(v);
    }

    deactivate(v) {
      this.active.delete(v);
      this.unload(v);
    }

    onCanPlay(e) {
      this.tryPlay(e.currentTarget);
    }

    tryPlay(v) {
      if (this.reduceMotion) return;
      const p = v.play();
      // Autoplay blocked / interrupted -> poster stays, no uncaught rejection.
      if (p && typeof p.catch === 'function') p.catch(() => {});
    }

    load(v) {
      if (!v.getAttribute('src') && v.dataset.src) v.setAttribute('src', v.dataset.src);
    }

    // Pause AND unload offscreen to free the WKWebView decode buffer.
    unload(v) {
      v.removeEventListener('canplay', this._onCanPlay);
      try {
        v.pause();
        v.removeAttribute('src');
        v.load(); // forces the decoder to release the buffer
      } catch (e) {
        /* no-op */
      }
    }
  }

  customElements.define('lm-seen-on-ig', LmSeenOnIg);
}
